import React from "react";
import { useMutation } from "@apollo/client";
import { useForm, Controller } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { zodResolver } from "@hookform/resolvers/zod";

import { FormGroup } from "@cofano/cck";

import { FormDialog } from "components/dialogs/Dialogs";
import NumberInput from "components/cck/NumberInput";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import { DataFormat, Language } from "imports/Utils";

import type { DeclarationRow } from "view/declarations/components/table/DeclarationsTable";
import VatCodeSelect from "view/vatcodes/components/selects/VatCodeSelect";
import { advancePaymentSchema, AdvancePaymentSchemaInput, AdvancePaymentSchemaOutput } from "view/invoices/utils/invoice.schema";

import { CreateAdvancePaymentDocument } from "__generated__/view/invoices/graphql/invoices.mutations.generated";

interface AdvancePaymentDialogProps {
	open: boolean;
	onClose: () => void;
	charterId: number;
	currency: string;
	currentTotal: number;
	onCreated: (declaration: DeclarationRow) => void;
}

const AdvancePaymentDialog = ({ open, onClose, charterId, currency, currentTotal, onCreated }: AdvancePaymentDialogProps) => {

	const [ createAdvancePayment, { loading } ] = useMutation(CreateAdvancePaymentDocument);

	const defaultValues = {
		amount: null,
		vatCode: null,
	};

	const { control, handleSubmit, watch, reset } = useForm<AdvancePaymentSchemaInput, unknown, AdvancePaymentSchemaOutput>({
		resolver: zodResolver(advancePaymentSchema),
		defaultValues: defaultValues,
	});

	const newTotal = currentTotal + (watch("amount") ?? 0);

	const handleClose = () => {
		onClose();
		reset(defaultValues);
	};

	const save = async (data: AdvancePaymentSchemaOutput) => {
		createAdvancePayment({
			variables: {
				charterId: charterId,
				amount: data.amount,
				vatCodeId: data.vatCode.id,
			},
		}).then((response) => {
			if (response.data?.createAdvancePayment) {
				onCreated(response.data?.createAdvancePayment);
			}

			handleClose();
		});
	};

	return (
		<FormDialog
			open={open}
			onClose={handleClose}
			title={intl.formatMessage({ id: "NEW_ADVANCE_PAYMENT" })}
			maxWidth="xs"
			form="advancePaymentForm"
			loading={loading}>
			<form onSubmit={(event) => void handleSubmit(save)(event)} id="advancePaymentForm">
				<FormGroup label={Language("ADVANCE_PAYMENT")} labelPlacement="left-center" required>
					<Controller
						name="amount"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<NumberInput
									{...field}
									decimalScale={2}
									addonBefore={DataFormat.currencySign(currency)} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>

				<FormGroup label={Language("VAT_CODE")} labelPlacement="left-center" required>
					<Controller
						name="vatCode"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<VatCodeSelect {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>

				<FormGroup label={Language("NEW_TOTAL_AMOUNT")} labelPlacement="left-center">
					<span>{DataFormat.price(newTotal)}</span>
				</FormGroup>
			</form>
		</FormDialog>
	);
};

export default AdvancePaymentDialog;
